import React, { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';

import SiteNav from '../SiteNav';
import LegalFooter from '../LegalFooter';

import { useGetWebleadFromCookie } from '../../customHooks';

import { checkoutStarted } from '../../klaviyoActions';
import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import styles from './EFuneralPurchase.module.scss';

const { updateWebleadData, resetWebleadUpdated } = authUserOperations;

const {
  'e-funeral-purchase': eFuneralPurchaseClass,
  'e-funeral-purchase-content': eFuneralPurchaseContentClass,
  'e-funeral-iframe': eFuneralIFrameClass,
  'everdays-better-footer': everdaysBetterFooterClass,
  // eFuneral Page Classes
  instructions: instructionsClass,
  'confirm-details': confirmDetailsClass,
  beneficiary: beneficiaryClass,
  'add-beneficiary': addBeneficiaryClass,
  summary: summaryClass,
  payment: paymentClass,
  'ssn-capture': ssnCaptureClass,
  identity: identityClass,
  'identity-failed': identityFailedClass,
  'purchaser-order-summary': purchaserOrderSummaryClass,
  signing: signingClass
} = styles;

const EFuneralPurchase = () => {
  const [currentStep, setCurrentStep] = useState('');

  const dispatch = useDispatch();

  const { authUser = {}, purchaseFlow = {} } = useSelector(({ authUser, webPurchase }) => ({
    authUser,
    purchaseFlow: webPurchase.purchaseFlow
  }));

  // assign most prioritized user data
  const { data: userData } = useMemo(() => {
    return !!authUser?.data?._id ? authUser : purchaseFlow;
  }, [authUser, purchaseFlow]);

  const { _id: userId, eFuneral } = userData || {};
  const {
    packageFundingSigned,
    packageData: { name: packageName } = {},
    paymentOption,
    funeralHomeId
  } = eFuneral || {};
  const { years: termYears, premiumAmount: priceMonthly, faceAmount: priceTotal } =
    paymentOption ?? {};
  const { name: providerName, address: providerAddress } = funeralHomeId ?? {};

  const eFuneralProdUrl = 'https://agent.efuneral.com';
  const eFuneralDevUrl = 'https://stagingfuneral.dev';

  // on mount, check if weblead cookie exists and fetch existing weblead
  useGetWebleadFromCookie();

  // track sale started
  useEffect(() => {
    checkoutStarted({
      packageName,
      term: termYears * 12,
      priceMonthly,
      priceTotal,
      providerName,
      providerAddress
    });
  }, [packageName, priceMonthly, priceTotal, providerAddress, providerName, termYears]);

  // add listener to handle when a message is received from eFuneral
  useEffect(() => {
    const messageHandler = async e => {
      if (e.origin.startsWith(eFuneralDevUrl) || e.origin.startsWith(eFuneralProdUrl)) {
        const pageName = e?.data?.pageName;

        // handle signing step like this since it doesn't emit a pageName change
        if (pageName === 'Loading' && currentStep === 'Identity') {
          setCurrentStep('Signing');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // when the pageName changes and is not 'Loading' or '', update weblead
        // with new lastScreen, if 'Complete' send to confirmation
        if (pageName !== 'Loading' && pageName !== '') {
          setCurrentStep(pageName);
          window.scrollTo({ top: 0, behavior: 'smooth' });

          const updatedUserBody = {
            eFuneral: {
              ...(pageName === 'Complete' && { packageFundingSigned: true }),
              lastScreen: {
                flow: 'eFuneral',
                screen: pageName,
                date: new Date().toISOString()
              }
            }
          };

          if (authUser?.data?._id) {
            await dispatch(updateWebleadData(updatedUserBody));
            await dispatch(resetWebleadUpdated());
          } else {
            await dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
          }

          if (pageName === 'Complete') {
            browserHistory.push('/b/purchase-confirmation');
          }
        }
      }
    };

    if (userId) {
      window.addEventListener('message', messageHandler);
    }

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [userId, dispatch, currentStep]);

  // once completion meta data is detected navigate to purchase confirmation/completed page
  useEffect(() => {
    if (!!packageFundingSigned) {
      browserHistory.push('/b/purchase-confirmation');
    }
  }, [packageFundingSigned]);

  const iframeClasses = classNames(eFuneralIFrameClass, {
    [instructionsClass]: currentStep === 'Instructions',
    [confirmDetailsClass]: currentStep === 'Confirm Details',
    [beneficiaryClass]: currentStep === 'Beneficiary',
    [addBeneficiaryClass]: currentStep === 'Add Beneficiary',
    [summaryClass]: currentStep === 'Summary',
    [paymentClass]: currentStep === 'Payment',
    [ssnCaptureClass]: currentStep === 'SSNCapture',
    [identityClass]: currentStep === 'Identity',
    [identityFailedClass]: currentStep === 'IdentityFailed',
    [purchaserOrderSummaryClass]: currentStep === 'PurchaserOrderSummary',
    [signingClass]: currentStep === 'Signing'
  });

  return (
    <div className={eFuneralPurchaseClass}>
      <SiteNav />
      <div className={eFuneralPurchaseContentClass}>
        <iframe
          id="efuneral-iframe"
          className={iframeClasses}
          title="eFuneral Purchase"
          width="800"
          src={userData.eFuneral?.url}
        ></iframe>
        <div className={everdaysBetterFooterClass} />
      </div>
      <LegalFooter />
    </div>
  );
};

export default EFuneralPurchase;
